import { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

const isElectronApp = typeof window !== 'undefined' && (window.location.protocol === 'file:' || navigator.userAgent.toLowerCase().includes('electron'));
const AppRouter = isElectronApp ? HashRouter : BrowserRouter;
import { Toaster } from 'sonner';
import { useAuth } from './contexts/AuthContext';
import { isDesktopLoginEntry } from './utils/desktopLogin';

import { LandingPage } from './pages/LandingPage';
const UserDashboard = lazy(() => import('./pages/user/UserDashboard').then(m => ({ default: m.UserDashboard })));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const Auth = lazy(() => import('./pages/Auth').then(m => ({ default: m.Auth })));
const TechReader = lazy(() => import('./pages/TechReader').then(m => ({ default: m.TechReader })));
const DownloadPage = lazy(() => import('./pages/DownloadPage').then(m => ({ default: m.DownloadPage })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const Disclaimer = lazy(() => import('./pages/Disclaimer').then(m => ({ default: m.Disclaimer })));
const FAQ = lazy(() => import('./pages/FAQ').then(m => ({ default: m.FAQ })));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy').then(m => ({ default: m.CookiePolicy })));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy').then(m => ({ default: m.RefundPolicy })));
const AffiliateTerms = lazy(() => import('./pages/AffiliateTerms').then(m => ({ default: m.AffiliateTerms })));
const ReferralDashboard = lazy(() => import('./pages/user/ReferralDashboard').then(m => ({ default: m.ReferralDashboard })));
const Report = lazy(() => import('./pages/user/Report').then(m => ({ default: m.Report })));
const SkillTemplate = lazy(() => import('./pages/public/SkillTemplate').then(m => ({ default: m.SkillTemplate })));
const ProfileTemplate = lazy(() => import('./pages/public/ProfileTemplate').then(m => ({ default: m.ProfileTemplate })));
const CheatSheetTemplate = lazy(() => import('./pages/public/CheatSheetTemplate').then(m => ({ default: m.CheatSheetTemplate })));
const CheatSheetsIndex = lazy(() => import('./pages/public/CheatSheetsIndex').then(m => ({ default: m.CheatSheetsIndex })));
const VideoPage = lazy(() => import('./pages/VideoPage').then(m => ({ default: m.VideoPage })));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({ default: m.default })));
const AuthError = lazy(() => import('./pages/AuthError').then(m => ({ default: m.AuthError })));
const AppuntiModalWindow = lazy(() => import('./components/teleprompter/AppuntiModalWindow').then(m => ({ default: m.AppuntiModalWindow })));
const DesktopLoginPage = lazy(() => import('./pages/DesktopLoginPage').then(m => ({ default: m.DesktopLoginPage })));
const AccountPage = lazy(() => import('./pages/AccountPage').then(m => ({ default: m.AccountPage })));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess').then(m => ({ default: m.PaymentSuccess })));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback').then(m => ({ default: m.OAuthCallback })));
const SetupWizardHost = lazy(() => import('./components/setup/SetupWizardHost').then(m => ({ default: m.SetupWizardHost })));
const GlobalSupportBot = lazy(() => import('./components/ui/GlobalSupportBot').then(m => ({ default: m.GlobalSupportBot })));
const BackButton = lazy(() => import('./components/ui/BackButton').then(m => ({ default: m.BackButton })));
const AdminDebugModal = lazy(() => import('./components/AdminDebugModal').then(m => ({ default: m.AdminDebugModal })));
const DesktopAuthGate = lazy(() => import('./components/desktop/DesktopAuthGate').then(m => ({ default: m.DesktopAuthGate })));
const MousePassThrough = lazy(() => import('./components/desktop/MousePassThrough').then(m => ({ default: m.MousePassThrough })));
import { CookieConsent } from './components/consent/CookieConsent';
// WelcomeModal usa AppModal (Radix Dialog): tenerla lazy evita di portare il
// primitivo dialog (=~radix) nel critical path. Il Suspense e' gia' al suo mount.
const WelcomeModal = lazy(() => import('./components/ui/WelcomeModal').then(m => ({ default: m.WelcomeModal })));
const JarvisDesktopBar = lazy(() => import('./components/desktop/JarvisDesktopBar').then(m => ({ default: m.JarvisDesktopBar })));
const GhostCursor = lazy(() => import('./components/GhostCursor').then(m => ({ default: m.GhostCursor })));
import { ErrorBoundary } from './components/ErrorBoundary';
const GoogleOneTapProvider = lazy(() => import('./components/auth/GoogleOneTapProvider').then(m => ({ default: m.GoogleOneTapProvider })));
import { GoogleAnalytics } from './components/SEO/GoogleAnalytics';
import { RouteMeta } from './components/SEO/RouteMeta';
import { ScrollToTop } from './components/layout/ScrollToTop';
import { useTelemetryHeartbeat } from './hooks/useTelemetryHeartbeat';

function SecureRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const langMatch = location.pathname.match(/^\/([a-z]{2})(?:\/|$)/);
  const lang = langMatch ? langMatch[1] : 'it';
  
  if (!user) {
    return <Navigate to={`/${lang}/login`} replace />;
  }
  return <>{children}</>;
}

/**
 * [T7 - ElectronGuard]
 * Quando Jarvis viene avviato in Electron (protocol === file:) e l'utente è già loggato,
 * bypassa la LandingPage e porta direttamente all'ultima dashboard visitata.
 * lastProfile viene salvato in localStorage da UserDashboard al mount.
 */
function ElectronGuard({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticating, needsElectronLogin } = useAuth();
  const location = useLocation();

  const isElectron = typeof window !== 'undefined' && (window.location.protocol === 'file:' || navigator.userAgent.toLowerCase().includes('electron'));

  if (!isElectron || isAuthenticating) return <>{children}</>;

  // Electron prod senza sessione reale: gate di login esplicito con stati
  // guidati (attesa browser, timeout, successo) invece di schermata statica.
  if (needsElectronLogin) {
    return <DesktopAuthGate />;
  }

  if (user) {
    const langMatch = location.pathname.match(/^\/([a-z]{2})(?:\/|$)/);
    const lang = langMatch ? langMatch[1] : 'it';
    const lastProfile = localStorage.getItem('jarvis_last_profile') || 'generic';
    return <Navigate to={`/${lang}/app/${lastProfile}`} replace />;
  }

  return <>{children}</>;
}

function AdminRoute() {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/" replace />;
  return <AdminDashboard />;
}

import { LightLoader } from './components/LightLoader';

function RouteFallback() {
  return <LightLoader text="Caricamento Moduli..." />;
}

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nProvider } from '@genome/i18n/src/contexts/I18nContext';

import { useI18n } from './contexts/I18nContext';

function LangRedirect() {
  const { lang } = useI18n();
  const location = useLocation();
  const path = location.pathname === '/' ? '/' : location.pathname;
  return <Navigate to={`/${lang}${path}${location.search}`} replace />;
}

function RootRedirect() {
  return <ElectronGuard><LandingPage /></ElectronGuard>;
}

function LoginRedirect() {
  const { lang } = useI18n();
  return <Navigate to={`/${lang}/login`} replace />;
}

function ReferralCapture() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('jarvis_ref_code', ref);
    }
  }, [location]);
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

import { useVersionEnforcer } from './hooks/useVersionEnforcer';
import { useVersionBanner } from './hooks/useVersionBanner';
const VersionBanner = lazy(() => import('./components/VersionBanner').then(m => ({ default: m.VersionBanner })));

/**
 * [STEP 5] AppShell: rileva se siamo in una finestra Electron figlia (route /electron/*).
 * In tal caso renderizza un layout minimale (solo la modale, niente desktop bar/sidebar/cookie).
 * Altrimenti renderizza i children (l'app completa con tutto il chrome, passati da App).
 */
function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  if (location.pathname.startsWith('/electron/')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <ErrorBoundary>
          <Routes>
            <Route path="/electron/appunti-modal" element={<AppuntiModalWindow />} />
            <Route path="*" element={<Navigate to="/electron/appunti-modal" replace />} />
          </Routes>
        </ErrorBoundary>
      </Suspense>
    );
  }
  return <>{children}</>;
}

function App() {
  useTelemetryHeartbeat();
  useVersionEnforcer();
  const versionBanner = useVersionBanner();
  const { user, isAuthenticating, isAdmin } = useAuth();
  const [isDebugOpen, setIsDebugOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'd') {
        if (isAdmin) {
          e.preventDefault();
          setIsDebugOpen(prev => !prev);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAdmin]);

  // Login desktop da browser: con ?desktop_challenge=... la SPA non deve
  // montare admin/landing (confusione "sito vs app"): solo la pagina
  // interstitial brandizzata che rilancia il deep link verso l'app.
  // I18nProvider richiede un Router sopra (useLocation): riusiamo AppRouter.
  const isDesktopLoginPage =
    typeof window !== 'undefined' &&
    (!isElectronApp) &&
    isDesktopLoginEntry({
      search: window.location.search,
      hash: window.location.hash,
      hasStoredChallenge: (() => { try { return !!sessionStorage.getItem('jarvis_desktop_challenge'); } catch { return false; } })(),
    });
  if (isDesktopLoginPage) {
    return (
      <AppRouter>
        <I18nProvider>
          <Suspense fallback={<LightLoader text="Avvio Accesso Desktop..." />}>
            <DesktopLoginPage />
          </Suspense>
        </I18nProvider>
      </AppRouter>
    );
  }

  if (isAuthenticating) {
    return <LightLoader text="Sincronizzazione Sessione..." />;
  }

  // [T7 - Electron non autenticato] In Electron (file:) senza utente NON si deve
  // MAI renderizzare una pagina web pubblica (landing o '/:lang/login' con header
  // marketing): si mostra SEMPRE il DesktopAuthGate dedicato, qualunque sia la
  // rotta (un redirect a '/:lang/login' dopo un 401 non deve far apparire la
  // pagina web Auth). Escluse le finestre figlie '/electron/*' (post-login).
  const isElectronChildWindow =
    typeof window !== 'undefined' && window.location.hash.startsWith('#/electron/');
  if (isElectronApp && !user && !isElectronChildWindow) {
    return (
      <AppRouter>
        <I18nProvider>
          <Suspense fallback={<LightLoader text="Avvio Accesso Desktop..." />}>
            <DesktopAuthGate />
          </Suspense>
        </I18nProvider>
      </AppRouter>
    );
  }

  return (
<QueryClientProvider client={queryClient}>
        <GoogleAnalytics />
        <AppRouter>
          <Toaster position="bottom-right" theme="system" richColors closeButton />
          {versionBanner && <VersionBanner version={versionBanner.version} onReload={versionBanner.reload} />}
<I18nProvider>
          <RouteMeta />
          <ScrollToTop />
          <AppShell>
          <ErrorBoundary>
          <ReferralCapture />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/callback" element={<OAuthCallback />} />
            <Route path="/:lang" element={<ElectronGuard><LandingPage /></ElectronGuard>} />
            <Route path="/:lang/login" element={<Auth />} />
            <Route path="/:lang/auth-error" element={<AuthError />} />
            <Route path="/:lang/payment/success" element={<PaymentSuccess />} />
            <Route path="/:lang/app/:profileId" element={<SecureRoute><UserDashboard /></SecureRoute>} />
            <Route path="/:lang/account" element={<SecureRoute><AccountPage /></SecureRoute>} />
            <Route path="/:lang/admin/:profileId" element={<SecureRoute><AdminRoute /></SecureRoute>} />
            <Route path="/:lang/tutorial/:tech" element={<TechReader />} />
            <Route path="/:lang/download" element={<DownloadPage />} />
            <Route path="/:lang/skills/:slug" element={<SkillTemplate />} />
            <Route path="/:lang/profiles/:slug" element={<ProfileTemplate />} />
            <Route path="/:lang/cheatsheet/:slug" element={<CheatSheetTemplate />} />
            <Route path="/:lang/cheatsheets" element={<CheatSheetsIndex />} />
            <Route path="/:lang/video/:id" element={<VideoPage />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/:lang/referral" element={<SecureRoute><ReferralDashboard /></SecureRoute>} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/affiliate-terms" element={<AffiliateTerms />} />

            {/* Varianti localizzate delle pagine legali: NON stanno in sitemap (che elenca solo le
                root /terms, /faq, ecc. finche' il contenuto non e' tradotto). Senza queste, un link
                diretto a /:lang/faq cade nel catch-all "*" e mostra la landing. */}
            <Route path="/:lang/terms" element={<Terms />} />
            <Route path="/:lang/privacy" element={<Privacy />} />
            <Route path="/:lang/disclaimer" element={<Disclaimer />} />
            <Route path="/:lang/faq" element={<FAQ />} />
            <Route path="/:lang/cookies" element={<CookiePolicy />} />
            <Route path="/:lang/refund" element={<RefundPolicy />} />
            <Route path="/:lang/affiliate-terms" element={<AffiliateTerms />} />
            
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginRedirect />} />
            <Route path="/tutorial/*" element={<LangRedirect />} />
            <Route path="/app/*" element={<LangRedirect />} />
            <Route path="/admin/*" element={<LangRedirect />} />
            
            <Route path="/:lang/app" element={<Navigate to="generic" replace />} />
            <Route path="/:lang/admin" element={<Navigate to="generic" replace />} />
            
            <Route path="/:lang/interviews/report/:id" element={<SecureRoute><Report /></SecureRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
          <Suspense fallback={null}><WelcomeModal /></Suspense>
          <Suspense fallback={null}><GoogleOneTapProvider /></Suspense>
          <CookieConsent />
          <GlobalSupportBot />
          {/* Back globale: solo su pagine app profonde senza nav propria (vedi BackButton). */}
          <Suspense fallback={null}><BackButton /></Suspense>
          {/* Widget desktop/Electron: sul web pubblico non devono nemmeno scaricare il chunk. */}
          {isElectronApp && (
            <>
              <Suspense fallback={null}><SetupWizardHost /></Suspense>
              <Suspense fallback={null}><JarvisDesktopBar /></Suspense>
              {/* GhostCursor dopo la barra: a parita' di z-index (int32 max) il paint order lo tiene sopra */}
              <Suspense fallback={null}><GhostCursor /></Suspense>
              <MousePassThrough />
            </>
          )}
          {isDebugOpen && <AdminDebugModal onClose={() => setIsDebugOpen(false)} />}
        </AppShell>
      </I18nProvider>
      </AppRouter>
    </QueryClientProvider>
  );
}

export default App;
